import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getCriancaAtiva, listarAlimentos } from "../../db/queries";
import { baixarCSV } from "../../lib/export";
import { dataNascimentoFmt, idade } from "../../lib/format";
import { ESPECIALIDADE_LABEL, type EspecialidadeProf } from "../../types";
import { notasParaCSV } from "../export";
import { getNotasDaCrianca, listarProfissionais } from "../queries";
import type { NotaSessao } from "../types";
import { TimelineEquipe } from "../ui/TimelineEquipe";

/** P0.6 — relatório interdisciplinar legível por profissional + PDF (client-side). */
const PERIODOS: { v: number | "tudo"; r: string }[] = [
  { v: 30, r: "últimos 30 dias" },
  { v: 90, r: "últimos 90 dias" },
  { v: 180, r: "últimos 6 meses" },
  { v: "tudo", r: "todo o histórico" },
];

export function RelatorioElo() {
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const profissionais = useLiveQuery(() => listarProfissionais(true)) ?? [];
  const alimentos = useLiveQuery(() => listarAlimentos(true)) ?? [];
  const notas = useLiveQuery(
    () => (crianca ? getNotasDaCrianca(crianca.id) : Promise.resolve([])),
    [crianca?.id],
  ) ?? [];
  const [dias, setDias] = useState<number | "tudo">("tudo");
  const [feito, setFeito] = useState(false);

  const inicioMs = dias === "tudo" ? 0 : Date.now() - dias * 86400000;
  const noPeriodo = (n: NotaSessao) => new Date(n.data_sessao).getTime() >= inicioMs;
  const notasPeriodo = notas.filter(noPeriodo);

  // profissionais que acompanharam (por nº de sessões)
  const porProf = (() => {
    const map = new Map<string, { nome: string; esp: EspecialidadeProf; n: number }>();
    for (const n of notasPeriodo) {
      const nome = profissionais.find((p) => p.id === n.profissional_id)?.nome ?? "profissional";
      const cur = map.get(n.profissional_id) ?? { nome, esp: n.especialidade, n: 0 };
      cur.n += 1;
      map.set(n.profissional_id, cur);
    }
    return [...map.values()].sort((a, b) => b.n - a.n);
  })();

  const nomePorProfissional = (id: string) =>
    profissionais.find((p) => p.id === id)?.nome ?? "profissional";
  const periodoLabel = PERIODOS.find((p) => p.v === dias)?.r ?? "período";
  const hojeFmt = new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  const nomePaciente = crianca?.nome_completo || crianca?.apelido || "paciente";
  const nascFmt = dataNascimentoFmt(crianca?.data_nascimento ?? null);
  const anos = idade(crianca?.data_nascimento ?? null);

  async function exportarCsv() {
    const csv = notasParaCSV(notasPeriodo, profissionais, alimentos);
    await baixarCSV(csv, `elo-sessoes-${new Date().toISOString().slice(0, 10)}.csv`);
    setFeito(true);
    setTimeout(() => setFeito(false), 2500);
  }

  return (
    <main className="tela relatorio">
      <div className="no-print">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>← voltar</button>
        <h1 className="tela-titulo">relatório</h1>
        <p className="tela-sub">a história interdisciplinar da criança, pra levar à equipe. nada sai do aparelho sem você gerar.</p>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <label>período</label>
            <select value={String(dias)} onChange={(e) => setDias(e.target.value === "tudo" ? "tudo" : Number(e.target.value))}>
              {PERIODOS.map((p) => <option key={String(p.v)} value={String(p.v)}>{p.r}</option>)}
            </select>
          </div>
        </div>
        <p className="secao-titulo" style={{ marginTop: "var(--s5)" }}>prévia do relatório</p>
      </div>

      <article className="folha">
        <header className="rel-head">
          <div>
            <h2 className="rel-titulo">relatório interdisciplinar</h2>
            <p className="rel-sub">{nomePaciente} · {periodoLabel}</p>
            {(nascFmt || anos !== null) && (
              <p className="rel-ident">nascimento: {nascFmt ?? "—"}{anos !== null ? ` · ${anos} ${anos === 1 ? "ano" : "anos"}` : ""}</p>
            )}
          </div>
          <div className="rel-data">gerado em {hojeFmt}</div>
        </header>

        <section className="rel-stats">
          <div className="rel-stat"><span className="n">{porProf.length}</span><span className="r">profissionais</span></div>
          <div className="rel-stat"><span className="n">{notasPeriodo.length}</span><span className="r">sessões</span></div>
          <div className="rel-stat"><span className="n">{new Set(notasPeriodo.map((n) => n.especialidade)).size}</span><span className="r">disciplinas</span></div>
        </section>

        {porProf.length > 0 && (
          <section className="rel-profissionais">
            <h3 className="rel-secao-titulo">profissionais que acompanharam</h3>
            <ul className="rel-prof-lista">
              {porProf.map((p, i) => (
                <li key={i}>
                  <span className="rel-prof-nome">{p.nome}</span>
                  <span className="rel-prof-esp"> · {ESPECIALIDADE_LABEL[p.esp]}</span>
                  <span className="rel-prof-n"> · {p.n} {p.n === 1 ? "sessão" : "sessões"}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {notasPeriodo.length === 0 ? (
          <div className="vazio">sem sessões registradas neste período.</div>
        ) : (
          <section style={{ marginTop: "var(--s4)" }}>
            <TimelineEquipe notas={notasPeriodo} nomePorProfissional={nomePorProfissional} />
          </section>
        )}

        <footer className="rel-rodape">
          {(crianca?.responsavel_nome || crianca?.responsavel_cpf) && (
            <p className="rel-cpf">
              {crianca?.responsavel_nome ? `responsável (pais/guardião): ${crianca.responsavel_nome}` : ""}
              {crianca?.responsavel_nome && crianca?.responsavel_cpf ? " · " : ""}
              {crianca?.responsavel_cpf ? `CPF: ${crianca.responsavel_cpf}` : ""}
            </p>
          )}
          <p>escada de aproximação (0–9) como idioma das disciplinas: TO destrava 0–4, fono 5–9, psicologia reduz o teto emocional, nutrição escolhe o degrau. anexos sensíveis não entram neste relatório.</p>
          <p>gerado pelo Elo (Degrau) — conecta e organiza o cuidado; não prescreve nem substitui o profissional.</p>
        </footer>
      </article>

      <div className="no-print" style={{ marginTop: "var(--s5)" }}>
        <p className="secao-titulo">exportar</p>
        <button className="btn btn-primario btn-bloco btn-grande" disabled={notasPeriodo.length === 0} onClick={() => window.print()}>🖨️ gerar PDF</button>
        <button className="btn btn-fantasma btn-bloco" style={{ marginTop: "var(--s2)" }} disabled={notasPeriodo.length === 0} onClick={exportarCsv}>exportar dados crus (csv)</button>
        {feito && <p style={{ textAlign: "center", color: "var(--accent-forte)" }}>csv exportado ✓</p>}
        <p className="meta" style={{ color: "var(--text-muted)", marginTop: "var(--s2)" }}>o backup completo (.sqlite, inclui tudo) fica no relatório do Pitada (alimentar).</p>
      </div>
    </main>
  );
}
